import {
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { ArrowLeft, Search, Plus, Check, Loader2, X } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import featureGatingService from "../services/featureGatingService";
import {
  TEAM_ICONS,
  TEAM_COLORS,
  DEFAULT_TEAM_ICON,
  DEFAULT_TEAM_COLOR,
} from "./crm/teams/teamTheme";

const initialsOf = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

function CreateTeamForm({ onTeamCreated, onCancel }) {
  const { globalUser } = useAuth();
  const [teamName, setTeamName] = useState("");
  const [description, setDescription] = useState("");
  const [teamIcon, setTeamIcon] = useState(DEFAULT_TEAM_ICON);
  const [teamColor, setTeamColor] = useState(DEFAULT_TEAM_COLOR);
  const [selected, setSelected] = useState([]); // [{ uid, name, email }]
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);

  useEffect(() => {
    if (!globalUser?.uid) return;
    featureGatingService
      .canAddTeamMember(globalUser.uid)
      .then(setLimitInfo)
      .catch((e) => console.error("limit info:", e));
  }, [globalUser]);

  // Debounced member search (registered lawyers by name / email prefix).
  useEffect(() => {
    const handle = setTimeout(async () => {
      const q = term.trim();
      if (!q) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const end = q + String.fromCharCode(0xf8ff);
        const byId = new Map();
        for (const attr of ["name", "email"]) {
          const snap = await getDocs(
            query(
              collection(db, "lawyers"),
              where(attr, ">=", q),
              where(attr, "<=", end),
              limit(8)
            )
          );
          snap.forEach((d) => {
            if (d.id !== globalUser?.uid)
              byId.set(d.id, { uid: d.id, ...d.data() });
          });
        }
        setResults([...byId.values()]);
      } catch (e) {
        console.error("member search:", e);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [term, globalUser]);

  const isSelected = (uid) => selected.some((s) => s.uid === uid);

  const toggleMember = (u) => {
    if (isSelected(u.uid)) {
      setSelected((prev) => prev.filter((s) => s.uid !== u.uid));
      return;
    }
    if (limitInfo && !limitInfo.canAdd) {
      toast.error(limitInfo.upgradeMessage || "Team member limit reached.");
      return;
    }
    setSelected((prev) => [
      ...prev,
      { uid: u.uid, name: u.name || "", email: u.email || "" },
    ]);
  };

  const handleCreate = async () => {
    if (!globalUser) {
      toast.error("You must be logged in.");
      return;
    }
    if (!teamName.trim()) {
      toast.error("Team name is required.");
      return;
    }
    setCreating(true);
    try {
      const name =
        teamName.trim().charAt(0).toUpperCase() + teamName.trim().slice(1);
      const ref = await addDoc(collection(db, "teams"), {
        teamName: name,
        description: description.trim(),
        teamIcon,
        teamColor,
        ownerId: globalUser.uid,
        members: selected.map((s) => s.uid),
        createdAt: serverTimestamp(),
      });
      toast.success("Team created");
      if (onTeamCreated) onTeamCreated(ref.id, name);
    } catch (e) {
      console.error("Error creating team:", e);
      toast.error("Failed to create team.");
    } finally {
      setCreating(false);
    }
  };

  // Selected members pinned first, then search results not yet selected.
  const rows = [
    ...selected.map((s) => ({ ...s, _selected: true })),
    ...results
      .filter((r) => !isSelected(r.uid))
      .map((r) => ({ uid: r.uid, name: r.name, email: r.email, _selected: false })),
  ];

  const labelCls = "block text-sm font-medium text-slate-600 mb-1.5";
  const fieldCls =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40";

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
          Create New Team
        </h1>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* ===== Left: team details ===== */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div>
            <label className={labelCls}>
              Team Name <span className="text-red-500">*</span>
            </label>
            <input
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter team name"
              className={fieldCls}
            />
          </div>

          <div className="mt-4">
            <label className={labelCls}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter team description..."
              rows={3}
              className={`${fieldCls} resize-none`}
            />
          </div>

          <div className="mt-5">
            <label className={labelCls}>Team Icon</label>
            <div className="flex flex-wrap gap-2.5 pt-1.5">
              {TEAM_ICONS.map(({ key, Icon }) => {
                const active = teamIcon === key;
                return (
                  <div key={key} className="relative">
                    <button
                      type="button"
                      onClick={() => setTeamIcon(key)}
                      className={`flex h-11 w-11 items-center justify-center rounded-xl border transition-colors ${
                        active
                          ? "border-yellow-400 bg-yellow-50 ring-2 ring-yellow-400/40"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                      style={active ? { color: teamColor } : undefined}
                    >
                      <Icon className={`h-5 w-5 ${active ? "" : "text-slate-500"}`} />
                    </button>
                    {active && (
                      <button
                        type="button"
                        onClick={() => setTeamIcon("none")}
                        title="Remove icon"
                        className="absolute -right-2 -top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 shadow hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                      >
                        <X className="h-3 w-3" strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-5">
            <label className={labelCls}>Team Color</label>
            <div className="flex flex-wrap gap-3">
              {TEAM_COLORS.map((c) => {
                const active = teamColor === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setTeamColor(c)}
                    title={c}
                    className={`flex h-7 w-7 items-center justify-center rounded-full transition-transform hover:scale-110 ${
                      active ? "ring-2 ring-offset-2" : ""
                    }`}
                    style={{ backgroundColor: c, boxShadow: active ? `0 0 0 2px #fff, 0 0 0 4px ${c}` : undefined }}
                  >
                    {active && <Check className="h-4 w-4" style={{ color: "#ffffff" }} />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ===== Right: add members ===== */}
        <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <label className={labelCls}>Add Members</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search member..."
              className={`${fieldCls} pl-9`}
            />
          </div>

          <div className="mt-3 min-h-[220px] flex-grow space-y-1.5 overflow-y-auto pr-1">
            {searching && (
              <div className="flex items-center justify-center py-6 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}
            {!searching && rows.length === 0 && (
              <p className="py-8 text-center text-sm text-slate-400">
                {term.trim()
                  ? "No matching members found."
                  : "Search by name or email to add members."}
              </p>
            )}
            {rows.map((u) => (
              <div
                key={u.uid}
                className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2"
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                  style={{ backgroundColor: `${teamColor}1A`, color: teamColor }}
                >
                  {initialsOf(u.name || u.email)}
                </span>
                <div className="min-w-0 flex-grow">
                  <p className="truncate text-sm font-medium text-slate-800">
                    {u.name || "Unnamed"}
                  </p>
                  <p className="truncate text-xs text-slate-400">{u.email || "No email"}</p>
                </div>
                <button
                  type="button"
                  onClick={() => toggleMember(u)}
                  title={u._selected ? "Remove" : "Add"}
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${
                    u._selected
                      ? "bg-emerald-500 text-white hover:bg-emerald-600"
                      : "border border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {u._selected ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </button>
              </div>
            ))}
          </div>

          {selected.length > 0 && (
            <p className="mt-2 text-xs text-slate-500">
              {selected.length} member{selected.length === 1 ? "" : "s"} selected
            </p>
          )}

          {/* Actions */}
          <div className="mt-4 flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={creating || !teamName.trim()}
              onClick={handleCreate}
              className="inline-flex items-center gap-2 rounded-lg bg-yellow-500 px-5 py-2 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-yellow-600 disabled:opacity-50"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create Team
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateTeamForm;
